import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    Paper,
    Checkbox,
    FormControlLabel,
    IconButton,
    Fade,
    InputAdornment
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SendIcon from '@mui/icons-material/Send';
import MonacoEditor from "@monaco-editor/react";
import { useDispatch } from 'react-redux';
import { fetchCustomers } from '../../../redux/Slice/customerSlice';
import { addProductTemplate } from '../../../redux/Slice/ProductTemplate';
import { toast } from 'sonner';
import { setLoading } from '../../../redux/Slice/loaderSlice';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const RAG_API_URL = process.env.REACT_APP_RAG_URL;

const AddProductTemplate = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const promptRef = useRef(null);

    const [formData, setFormData] = useState({
        prompt: '',
        templateName: '',
        templateJSON: '{}',
        status: true,
        isDefault: false,
    });

    const [showChatPrompt, setShowChatPrompt] = useState(false);
    const [tempPrompt, setTempPrompt] = useState('');

    useEffect(() => {
        dispatch(fetchCustomers());
    }, [dispatch]);

    const handleChange = (field) => (event) => {
        const value =
            field === 'status' || field === 'isDefault'
                ? event.target.checked
                : event.target.value;

        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleEditorChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            templateJSON: value,
        }));
    };

    const handlePromptSubmit = async (promptText = formData.prompt) => {
        try {
            dispatch(setLoading(true));
            const payload = {
                system_prompt: `You are a JSON Schema Generator. Respond with a complete JSON Schema using Draft 2020-12 format. Your output must include keys like "$schema", "$id", "title", "type", and a detailed "properties" section. Each property should have appropriate types (e.g., string, number, boolean, object), and use enums, descriptions, required fields, and nested objects where applicable, ,don't give a schema field, title fields has alway Product only that only stored.`,
                user_prompt: promptText,
                additional_context: "",
                schema: formData.templateJSON || "{}",
            };

            const response = await axios.post(`${RAG_API_URL}/api/v1/text-to-text`, payload, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                }
            });

            if (response.data) {
                setFormData((prev) => ({
                    ...prev,
                    prompt: promptText,
                    templateJSON: JSON.stringify(response.data, null, 2),
                }));
                toast.success('Template regenerated!');
                setTempPrompt('');
                setShowChatPrompt(false);
                dispatch(setLoading(false)); 
            }
        } catch (error) {
            console.error('Prompt submission error:', error);
            toast.error('Failed to regenerate template');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        dispatch(setLoading(true));
        await dispatch(addProductTemplate({ ...formData }));
        toast.success('Template added successfully');
        navigate('/datahub/product/template');
        dispatch(setLoading(false));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (promptRef.current && !promptRef.current.contains(event.target)) {
                setTempPrompt('');
                setShowChatPrompt(false);
            }
        };

        if (showChatPrompt) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showChatPrompt]);

    const canRegenerate = formData.prompt.trim() && formData.templateJSON.trim();

    return (
        <div className='content'>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
                    Add Product Template
                </Typography>

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
                >
                    <TextField
                        label="Template Name"
                        value={formData.templateName}
                        onChange={handleChange('templateName')}
                        required
                        fullWidth
                    />

                    <TextField
                        label="Initial Prompt"
                        value={formData.prompt}
                        onChange={handleChange('prompt')}
                        required
                        fullWidth
                        multiline
                        minRows={3}
                    />

                    <Button
                        variant="outlined"
                        onClick={() => handlePromptSubmit()}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Generate Template from Prompt
                    </Button>

                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Template JSON:
                    </Typography>

                    <Box sx={{ position: 'relative', border: '1px solid #ccc', borderRadius: 1 }}>
                        {!showChatPrompt && canRegenerate && (
                            <IconButton
                                onClick={() => setShowChatPrompt((prev) => !prev)}
                                sx={{
                                    position: 'absolute',
                                    bottom: 4,
                                    right: 10,
                                    backgroundColor: 'white',
                                    zIndex: 2,
                                    '&:hover': { backgroundColor: '#f0f0f0' }
                                }}
                                title="Regenerate with New Prompt"
                            >
                                <AutorenewIcon />
                            </IconButton>
                         )}

                        <MonacoEditor
                            height="300px"
                            language="json"
                            theme="light"
                            value={formData.templateJSON}
                            onChange={handleEditorChange}
                            options={{
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                            }}
                            sx={{ position: 'relative' }}
                        />
                    </Box>

                    {canRegenerate && (
                        <Fade in={showChatPrompt}>
                            <Box
                                ref={promptRef}
                                sx={{
                                    mt: 2,
                                    background: '#f9f9f9',
                                    border: '1px solid #ccc',
                                    borderRadius: 2,
                                    maxWidth: 500,
                                    alignSelf: 'flex-start',
                                    position: 'absolute',
                                    top: 'calc(100% + -50px)', 
                                    right: 140,
                                }}
                            >
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter new prompt..."
                                    value={tempPrompt}
                                    onChange={(e) => setTempPrompt(e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => handlePromptSubmit(tempPrompt)}
                                                    disabled={!tempPrompt.trim()}
                                                >
                                                    <SendIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    autoFocus={showChatPrompt} 
                                />
                            </Box>
                        </Fade>
                     )} 

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.status}
                                onChange={handleChange('status')}
                            />
                        }
                        label="Active Status"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.isDefault}
                                onChange={handleChange('isDefault')}
                            />
                        }
                        label="Set as Default (while enable View by all customers)"
                    />

                    <Button type="submit" variant="contained" sx={{ mt: 3, alignSelf: 'flex-start' }}>
                        Submit
                    </Button>
                </Box>
            </Paper>
        </div>
    );
};

export default AddProductTemplate;
